import { useEffect, useRef } from 'react';
import { useWebSocket } from '../../contexts/WebSocketContext';

/** The run events that change what a list of beats shows. */
const RUN_EDGES = new Set(['started', 'finished', 'failed', 'cancelled']);

/**
 * Call `reload` when a briefing run starts or ends, on any beat (or one beat,
 * when `beatId` is given). Bunched: several events inside `waitMs` are one
 * reload.
 *
 * LISTS OF BEATS LOADED ONCE. A scheduled run started and finished in the
 * background while the beats list said "running" forever and kept the old
 * story counts until a manual reload.
 */
export function useBeatRunRefresh(reload: () => void, beatId?: number, waitMs = 2_000): void {
  const { subscribe } = useWebSocket();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(reload);
  latest.current = reload;
  useEffect(() => {
    const off = subscribe((event: any) => {
      if (event?.event_type !== 'news_beat' || !RUN_EDGES.has(event.status)) return;
      if (beatId !== undefined && event.beat_id !== beatId) return;
      if (timer.current) return;
      timer.current = setTimeout(() => {
        timer.current = null;
        latest.current();
      }, waitMs);
    });
    return () => {
      off?.();
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [subscribe, beatId, waitMs]);
}

export default useBeatRunRefresh;
